import React from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import Footer from "salad/GrainFooter.js";
import { SectionHeading } from "components/misc/Headings";
import MobileHeaderOnly from "components/headers/light.js";

/* ===== 样式 ===== */
const HeaderWrap = styled.div`${tw`relative z-40`}`;
const PageBg = styled.div`
  ${tw`min-h-screen`};
  background-color: #fffbdc; /* 整页背景色 */
`;

const HeadingRow = tw.div`flex`;
const Heading = tw(SectionHeading)`text-gray-900 mb-10`;
const Text = styled.div`
  ${tw`text-lg text-gray-800`}
  p {
    ${tw`mt-2 leading-loose`}
  }
  h1 {
    ${tw`text-3xl font-bold mt-10`}
  }
  h2 {
    ${tw`text-2xl font-bold mt-8`}
  }
  h3 {
    ${tw`text-xl font-bold mt-6`}
  }
  ul {
    ${tw`list-disc list-inside`}
    li {
      ${tw`ml-2 mb-3`}
      p {
        ${tw`mt-0 inline leading-normal`}
      }
    }
  }
`;

export default ({ headingText = "GRAIN MEDITERRANEAN PRIVACY POLICY" }) => {

  // 可选：抽屉里的菜单
  const drawerLinks = (
      <nav onClick={(e) => e.stopPropagation()}>
        <a href="/">Home</a>
        <a href="/menu">Menu</a>
        <a href="/story">Our Story</a>
        <a href="https://grainmediterranean.redcatcloud.com.au/universal/register">Loyalty</a>
        <a href="/catering">Catering</a>
      </nav>
  );

  return (
      <>
        <PageBg>
          <HeaderWrap>
            <MobileHeaderOnly
                logoSrc="https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/LOGO%20for%20website/Blue.svg"
                orderHref="https://order.grainmediterranean.com/"
                links={drawerLinks}
            />
          </HeaderWrap>

          <AnimationRevealPage>
            <Container>
              <ContentWithPaddingXl>
                <HeadingRow>
                  <Heading>{headingText}</Heading>
                </HeadingRow>

                <Text>
                  {/* ================= Privacy Policy ================= */}
                  <h1>Privacy Policy</h1>
                  <p><strong>Effective Date:</strong> 7 October 2025</p>
                  <p>
                    Grain Mediterranean ("we", "our", "us") is committed to protecting your privacy. This policy explains how we collect,
                    use, and protect your personal data when you visit our website, use our mobile app, or engage with us in-store.
                  </p>

                  <h2>1. Who We Are</h2>
                  <p>
                    This Privacy Policy applies to GBB Holborn Limited (company number 15011782), trading as Grain Mediterranean (“we”, “our”, “us”).
                    <br/>Email: hello@grainm.com
                    <br/>Address: 10-14 Old Wallfields Pegs Lane, Hertford, Hertfordshire, United Kingdom, SG13 8EQ
                  </p>

                  <h2>2. What Data We Collect</h2>
                  <p>We may collect the following personal information:</p>
                  <ul>
                    <li>Identity data – name, email, phone number.</li>
                    <li>Location data – to facilitate deliveries or local store services.</li>
                    <li>Payment data – payment card details (processed securely by third-party providers).</li>
                    <li>Order history – items ordered, frequency, transaction details.</li>
                    <li>Preferences – such as dietary requirements or allergens.</li>
                  </ul>

                  <h2>3. How We Use Your Data</h2>
                  <p>We use your data for purposes including:</p>
                  <ul>
                    <li>To process your orders and payments.</li>
                    <li>To personalise your experience (e.g., favourite items, dietary preferences).</li>
                    <li>To improve our website, app, and services.</li>
                    <li>To send you updates, offers, or news if you have opted in.</li>
                    <li>To comply with legal and regulatory obligations.</li>
                  </ul>
                  <p><strong>Legal basis for processing:</strong></p>
                  <ul>
                    <li>Performance of contract (to deliver your orders).</li>
                    <li>Legitimate interests (to improve services, maintain security, and communicate with you).</li>
                    <li>Consent (for marketing and optional features).</li>
                  </ul>

                  <h2>4. Sharing Your Data</h2>
                  <p>We may share limited data with:</p>
                  <ul>
                    <li>Payment providers (for secure transactions).</li>
                    <li>Delivery and logistics partners (to fulfil orders).</li>
                    <li>Technology providers (loyalty systems, app hosting, cloud storage).</li>
                    <li>Legal/regulatory authorities (if required).</li>
                  </ul>
                  <p>We do not sell or rent your personal information.</p>

                  <h2>5. International Data Transfers</h2>
                  <p>
                    Some of our technology and storage partners may be located outside the UK/EEA. Where this happens, we ensure appropriate
                    safeguards are in place, such as UK-approved Standard Contractual Clauses (SCCs).
                  </p>

                  <h2>6. How Long We Keep Your Data</h2>
                  <p>We keep personal data for as long as necessary for the purposes collected, or to meet legal/regulatory requirements. For example:</p>
                  <ul>
                    <li>Order and payment records – kept for tax/accounting purposes.</li>
                    <li>Marketing data – until you opt out.</li>
                    <li>Dietary information – only retained as long as you actively use our services.</li>
                  </ul>

                  <h2>7. Protecting Sensitive Data (Dietary Preferences)</h2>
                  <ul>
                    <li>Storing securely on encrypted systems.</li>
                    <li>Only using it to personalise your order safely.</li>
                    <li>Never sharing with third parties for marketing or unrelated purposes.</li>
                  </ul>

                  <h2>8. Cookies</h2>
                  <p>We use cookies and similar technologies to improve your browsing and app experience.</p>
                  <ul>
                    <li>Essential cookies – required for orders and secure login.</li>
                    <li>Analytics cookies – help us improve performance.</li>
                    <li>Marketing cookies – only if you consent.</li>
                  </ul>
                  <p>A cookie banner will allow you to set your preferences.</p>

                  <h2>9. Your Rights</h2>
                  <p>Under UK GDPR you have the right to:</p>
                  <ul>
                    <li>Access your personal data.</li>
                    <li>Correct or update inaccurate data.</li>
                    <li>Request deletion of your data.</li>
                    <li>Object to or restrict certain processing.</li>
                    <li>Withdraw consent for marketing at any time.</li>
                    <li>Request a copy of your data (portability).</li>
                  </ul>
                  <p>
                    To exercise these rights, contact us at [insert email]. We will respond within 30 days.
                  </p>

                  <h2>10. Policy Updates</h2>
                  <p>
                    We may update this policy from time to time. If we make material changes, we will notify you via our website, app, or email
                    (depending on your engagement with us).
                  </p>
                </Text>
              </ContentWithPaddingXl>
            </Container>
            <Footer />
          </AnimationRevealPage>
        </PageBg>
      </>
  );
};
